import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { designSystem, Modal } from '@yola/ws-ui';
import bowser from 'yola-bowser';
import classNames from 'classnames';
import LimitBanner from 'src/js/modules/common/components/limit-banner';
import { DIALOG_WIDTH } from '../constants/common';
import { fieldNames } from '../constants/block-generation';
import { ENTER_KEY } from '../constants/key-codes';

const {
  ControlGroup,
  DialogHeader,
  Stack,
  Paragraph,
  Heading,
  SimpleSelectField,
  TextAreaField,
  ActionButton,
  IconButton,
} = designSystem;

export const BASE_DIALOG_HEIGHT = 578;

function BlockGenerationDialog({
  modalRef,
  captions,
  fields,
  isSubmitDisabled,
  offset,
  size,
  onSubmit,
  onCancel,
  onReady,
  onDragEnd,
  onResizeStop,
  upgradeBanner = null,
}) {
  const [modalRecalcKey, setModalRecalcKey] = useState(0);
  const { label: textAreaLabel, ...textAreaFieldProps } = fields[fieldNames.USER_INPUT] || {};
  const withTextArea = Boolean(textAreaLabel);

  const handleKeyDown = (event) => {
    if (isSubmitDisabled || event.key !== ENTER_KEY) {
      return;
    }

    const cmdPressed = bowser.macOS ? event.metaKey : event.ctrlKey;
    if (cmdPressed) {
      event.preventDefault();
      onSubmit();
    }
  };

  useEffect(() => {
    setModalRecalcKey((prev) => prev + 1);
    // eslint-disable-next-line yola/react-hooks/exhaustive-deps
  }, [withTextArea]);

  const basedOnContentModalProps = withTextArea
    ? {
        height: size.height || BASE_DIALOG_HEIGHT,
        minHeight: BASE_DIALOG_HEIGHT,
        width: size.width || DIALOG_WIDTH,
        resizable: true,
      }
    : {
        height: 'auto',
        width: DIALOG_WIDTH,
        resizable: false,
      };

  return (
    <Modal
      modalRef={modalRef}
      draggable
      key={modalRecalcKey} // Force recalculation of Modal height when content changes
      className="ws-block-generation-dialog"
      dragHandleSelector=".ws-drag-trigger"
      dragCancelSelector=".ws-ui-icon-button"
      floatingModalElementSelector=".ws-ui-base-select-option"
      overlay="none"
      fullscreen={bowser.mobile}
      offsetX={offset.x}
      offsetY={offset.y}
      minWidth={DIALOG_WIDTH}
      onOverlayClick={onCancel}
      handleCancel={onCancel}
      onReady={onReady}
      onDragEnd={onDragEnd}
      onKeyDown={handleKeyDown}
      onResizeStop={onResizeStop}
      {...basedOnContentModalProps}
    >
      <div className="ws-block-generation-dialog__wrapper">
        <div className="ws-drag-trigger">
          <DialogHeader
            paddingTop="spacing-s"
            paddingBottom="spacing-s"
            paddingLeft="spacing-m"
            paddingRight="spacing-m"
          >
            <Stack gap="spacing-2-xs">
              <Stack gap="spacing-3-xs">
                <div className="ws-block-generation-dialog__heading">
                  <Heading type="heading-3">{captions.title}</Heading>
                  <IconButton transparent glyph="close" size="medium" onClick={onCancel} />
                </div>
                <Paragraph>{captions.description}</Paragraph>
              </Stack>
              <SimpleSelectField
                openMenuOnFocus
                preventOverflow
                name={fieldNames.BLOCK_CATEGORY}
                className="ws-block-generation-dialog__category-select"
                {...fields[fieldNames.BLOCK_CATEGORY]}
              />
            </Stack>
          </DialogHeader>
        </div>
        <div className="ws-block-generation-dialog__content-group">
          {withTextArea && (
            <div className="ws-block-generation-dialog__content">
              <ControlGroup title={textAreaLabel} fullheight>
                <TextAreaField fullheight name={fieldNames.USER_INPUT} {...textAreaFieldProps} />
              </ControlGroup>
            </div>
          )}
          <div
            className={classNames('ws-block-generation-dialog__content', {
              'ws-block-generation-dialog__content--no-border': !upgradeBanner,
            })}
          >
            <Stack gap="spacing-xs">
              {upgradeBanner && <LimitBanner {...upgradeBanner} />}
              <ActionButton
                fullWidth
                appearance="accent"
                format="solid"
                size="large"
                label={captions.buttonLabel}
                disabled={isSubmitDisabled}
                onClick={onSubmit}
              />
            </Stack>
          </div>
        </div>
      </div>
    </Modal>
  );
}

BlockGenerationDialog.propTypes = {
  modalRef: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({ current: PropTypes.object })])
    .isRequired,
  captions: PropTypes.shape({
    title: PropTypes.string.isRequired,
    description: PropTypes.node.isRequired,
    buttonLabel: PropTypes.string.isRequired,
  }).isRequired,
  fields: PropTypes.object.isRequired,
  isSubmitDisabled: PropTypes.bool.isRequired,
  offset: PropTypes.shape({
    x: PropTypes.number.isRequired,
    y: PropTypes.number.isRequired,
  }).isRequired,
  size: PropTypes.shape({
    width: PropTypes.number,
    height: PropTypes.number,
  }).isRequired,
  onSubmit: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  onReady: PropTypes.func.isRequired,
  onDragEnd: PropTypes.func.isRequired,
  onResizeStop: PropTypes.func.isRequired,
  upgradeBanner: PropTypes.shape({
    current: PropTypes.number,
    total: PropTypes.number,
    captions: PropTypes.shape({
      title: PropTypes.string,
      buttonText: PropTypes.string,
    }),
    onButtonClick: PropTypes.func,
  }),
};

export default BlockGenerationDialog;
